import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { CheckCircle2, Mail, MonitorSmartphone, ShieldAlert, ShieldCheck, X } from "lucide-react";
import LegalAcceptanceModal from "./LegalAcceptanceModal";

/**
 * AccountSecurityPanel: Settings-page block showing:
 *  - email verification status (with "send link again" button)
 *  - device sessions list with revoke
 *  - legal-bundle acceptance status + re-accept button
 *  - cookie consent snapshot
 *
 * Mounted from Settings.jsx (business owners & staff). All API calls go
 * through the existing /api/account/* endpoints.
 */
export default function AccountSecurityPanel() {
  const { refresh } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);

  const load = () => {
    return api.get("/account/compliance")
      .then((r) => setSnapshot(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const sendVerify = () => {
    setBusy(true);
    return api.post("/auth/send-verification-email")
      .then(() => {
        toast.success("Verification email sent. Check your inbox.");
        return load();
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };

  const revoke = (sid) => {
    setBusy(true);
    return api.post(`/account/sessions/${sid}/revoke`)
      .then(() => {
        toast.success("Session revoked. That device must sign in again.");
        return load();
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };

  const acceptLegal = ({ legal_bundle_version }) => {
    setBusy(true);
    return api.post("/account/accept-legal", {
        legal_bundle_version,
        accept_business_terms: snapshot?.user?.requires_legal_reaccept !== false,
      })
      .then(() => {
        toast.success("Legal acceptance updated.");
        setLegalOpen(false);
        return load();
      })
      .then(() => { if (refresh) refresh(); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };

  if (loading) {
    return (
      <div data-testid="account-security-panel"
           className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        Loading account security…
      </div>
    );
  }
  if (!snapshot) return null;

  const u = snapshot.user || {};
  const sessions = snapshot.sessions || [];
  const consent = snapshot.cookie_consent;
  const needsReaccept = !!u.requires_legal_reaccept;
  const verified = !!u.email_verified;

  return (
    <div data-testid="account-security-panel"
         className="rounded-xl border border-slate-200 bg-white p-6 space-y-6 text-sm">
      <div>
        <h2 className="font-display text-lg font-semibold flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-700" /> Account security &amp; consent
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Manage verification, signed-in devices, legal acceptance, and cookie consent.
        </p>
      </div>

      {/* Email verification */}
      <section className="border border-slate-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-slate-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-slate-900">Email verification</div>
            {verified ? (
              <p className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1"
                 data-testid="account-email-verified">
                <CheckCircle2 className="w-3.5 h-3.5" /> Verified
                {u.email_verified_at && (
                  <span className="text-slate-500">· {new Date(u.email_verified_at).toLocaleString()}</span>
                )}
              </p>
            ) : (
              <p className="text-xs text-amber-700 mt-0.5" data-testid="account-email-unverified">
                Not verified yet. Some features may be limited until you verify your email.
              </p>
            )}
          </div>
          {!verified && (
            <button
              onClick={sendVerify}
              disabled={busy}
              data-testid="account-resend-verification"
              className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-medium hover:bg-slate-50 disabled:opacity-60">
              Send verification link
            </button>
          )}
        </div>
      </section>

      {/* Legal acceptance */}
      <section className="border border-slate-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          {needsReaccept
            ? <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            : <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5 flex-shrink-0" />}
          <div className="flex-1">
            <div className="font-medium text-slate-900">Legal terms</div>
            <p className="text-xs text-slate-600 mt-0.5">
              Accepted bundle: <span className="font-mono">{u.accepted_legal_bundle_version || "-"}</span>
              {" · "}Current: <span className="font-mono">{snapshot.current_legal_bundle_version}</span>
            </p>
            {needsReaccept && (
              <p className="text-xs text-amber-700 mt-1">
                Our legal terms have been updated. Please re-accept to keep using all features.
              </p>
            )}
          </div>
          <button
            onClick={() => setLegalOpen(true)}
            data-testid="account-reaccept-legal"
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-medium hover:bg-slate-50">
            {needsReaccept ? "Review & accept" : "Re-accept terms"}
          </button>
        </div>
      </section>

      {/* Device sessions */}
      <section className="border border-slate-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <MonitorSmartphone className="w-5 h-5 text-slate-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-slate-900">Signed-in devices</div>
            <p className="text-xs text-slate-500 mt-0.5">
              Each active session below has access to your account. Revoke any device you don&apos;t recognize.
            </p>
            <ul className="mt-3 space-y-2" data-testid="account-sessions-list">
              {sessions.length === 0 && (
                <li className="text-xs text-slate-500">No active sessions on file.</li>
              )}
              {sessions.map((s) => {
                const isCurrent = s.id === snapshot.current_session_id;
                return (
                  <li key={s.id}
                      data-testid={`account-session-${s.id}`}
                      className="rounded-lg border border-slate-200 px-3 py-2 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {s.device_label || (s.remembered ? "Remembered device" : "Browser session")}
                        {isCurrent && (
                          <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-emerald-700">
                            This device
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {s.user_agent || "Unknown user agent"}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        IP {s.ip || "-"} · last used{" "}
                        {s.last_used_at ? new Date(s.last_used_at).toLocaleString() : "-"}
                      </div>
                    </div>
                    {!isCurrent && (
                      <button
                        onClick={() => revoke(s.id)}
                        disabled={busy}
                        data-testid={`account-session-revoke-${s.id}`}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">
                        <X className="w-3 h-3" /> Revoke
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* Cookie consent */}
      <section className="border border-slate-200 rounded-lg p-4 text-xs text-slate-600"
               data-testid="account-cookie-consent">
        <div className="font-medium text-slate-900 text-sm">Cookie consent</div>
        {consent ? (
          <p className="mt-1">
            Last updated {new Date(consent.accepted_at).toLocaleString()}: categories:{" "}
            {Object.entries(consent.categories || {})
              .filter(([, v]) => v).map(([k]) => k).join(", ") || "essential only"}.
          </p>
        ) : (
          <p className="mt-1">No saved preference yet. The banner will appear on your next visit.</p>
        )}
      </section>

      <LegalAcceptanceModal
        open={legalOpen}
        onClose={() => setLegalOpen(false)}
        onAccept={acceptLegal}
        mode={(snapshot?.company && Object.keys(snapshot.company || {}).length) ? "business" : "customer"}
        title="Review and accept the current legal terms"
        subtitle="Read the summary below and confirm you agree to keep using ConfirmPH."
        acceptCta="I agree: record my acceptance"
        cancelable
      />
    </div>
  );
}
